/* eslint-disable @typescript-eslint/no-unused-expressions */
import TrafficLightComponent from "./TrafficLightComponent";
import { Card, CardHeader, CardTitle } from "./ui/card";
import { WorkstreamHealth } from "./StatusReport/StatusReportChart";
import { useNavigate } from "react-router-dom";
import { getImpactRating, getLifecycleStageName } from "@/lib/utils";
import { useContext } from "react";
import { AppContext } from "@/App";
import { useIsMobile } from "@/hooks/use-mobile";

// interface IWorkstreamHealth{  
//     workstreamName: string,  
//     overall: 2 | 1 |4,  
//     resources: 2 | 1 | 4,  
//     scope: 2 | 1 | 4,  
//     schedule: 2 | 1 | 4,  
//     financial: 2 | 1 | 4,
//     other: 2 | 1 | 4
// }  

export type WorkstreamHealthProps = {
    workstreamHealth: WorkstreamHealth[];
    isMultiProject?: boolean;
    showProjectName?: boolean;
}

function WorkstreamHealthReport({ workstreamHealth, isMultiProject = false, showProjectName = true }: WorkstreamHealthProps) {
    const app = useContext(AppContext);
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const showLifecycleStage = workstreamHealth.some(item => item.lifecycleStage !== undefined);

    function handleScheduleClick(workstreamId: number, filterCombo: number) {
        if (isMultiProject) {
            // in multi-project mode, workstreamId is the project ID
            app.setSelectedProject(app.projects.find(project => project.id === workstreamId)!);
            navigate('/Plan', { state: { filterCombo: filterCombo } });
        } else {
            navigate('/Plan', { state: { filterCombo: filterCombo, workstreamId: workstreamId } });
        }
    }

    function handleScopeClick(scope: 1 | 2 | 4, workstreamName?: string, workstreamId?: number) {
        if (isMultiProject) {
            app.setSelectedProject(app.projects.find(project => project.id === workstreamId)!);

            scope === 4 ? navigate('/Charter') : navigate('/Charter', {
                state: [
                    { id: "status", value: "Open" },
                    { id: "rating", value: getImpactRating(scope) }]
            })
        } else {
            scope === 4 ? navigate('/Charter') : navigate('/Charter', {
                state: [
                    { id: "workstream", value: workstreamName },
                    { id: "status", value: "Open" },
                    { id: "rating", value: getImpactRating(scope) }]
            })
        }
    }

    function veiwStatusReport(workstreamId: number, workstreamName?: string) {
        if (isMultiProject) {
            app.setSelectedProject(app.projects.find(project => project.id === workstreamId)!);
            navigate('/StatusReports', {
                state: { workstream: "Project" }
            });
        } else {
            navigate('/StatusReports', {
                state: { workstream: workstreamName }
            });
        }
    }

    function handleResourcesClick(workstreamId: number, resources: 1 | 2 | 4, workstreamName?: string) {
        if (isMultiProject) {
            app.setSelectedProject(app.projects.find(project => project.id === workstreamId)!);
            navigate('/SupportRequests', {
                state: [
                    { id: "status", value: "Open" },
                    { id: "rating", value: getImpactRating(resources) },
                    { id: "requestType", value: "Resource" }
                ]
            });
        } else {
            navigate('/SupportRequests', {
                state: [
                    { id: "workstream", value: workstreamName },
                    { id: "status", value: "Open" },
                    { id: "rating", value: getImpactRating(resources) },
                    { id: "requestType", value: "Resource" }
                ]
            });
        }
    }

    function handleFinancialClick(workstreamId: number, financial: 1 | 2 | 4, workstreamName?: string) {
        if (isMultiProject) {
            app.setSelectedProject(app.projects.find(project => project.id === workstreamId)!);
            navigate('/SupportRequests', {
                state: [
                    { id: "requestType", value: "Financial" },
                    { id: "status", value: "Open" },
                    { id: "rating", value: getImpactRating(financial) }
                ]
            });
        } else {
            navigate('/SupportRequests', {
                state: [
                    { id: "workstream", value: workstreamName },
                    { id: "requestType", value: "Financial" },
                    { id: "status", value: "Open" },
                    { id: "rating", value: getImpactRating(financial) }
                ]
            });
        }
    }

    function handleOtherClick(workstreamId: number, other: 1 | 2 | 4, workstreamName?: string) {
        if (isMultiProject) {
            app.setSelectedProject(app.projects.find(project => project.id === workstreamId)!);
            navigate('/SupportRequests', {
                state: [
                    { id: "requestType", value: "Other" },
                    { id: "status", value: "Open" },
                    { id: "rating", value: getImpactRating(other) }
                ]
            });
        } else {
            navigate('/SupportRequests', {
                state: [
                    { id: "workstream", value: workstreamName },
                    { id: "requestType", value: "Other" },
                    { id: "status", value: "Open" },
                    { id: "rating", value: getImpactRating(other) }
                ]
            });
        }
    }

    if (isMobile) {
        return (
            <Card className="flex grow flex-col justify-center px-2 pb-2">
                <CardHeader className="items-center pb-0">
                    <CardTitle className="text-4xl">{isMultiProject ? "Project" : "Workstream"} Health</CardTitle>
                </CardHeader>

                <div className="flex flex-col my-2">
                    {workstreamHealth && workstreamHealth.map((item, index) => (
                        <div key={index} className="flex flex-col border-b border-slate-300 last:border-b-0 pb-2 mb-2">
                            {/* First Row: Name, Stage, Overall */}
                            <div className="flex flex-row items-center gap-2 p-4 justify-between">
                                <div className="flex-1 truncate font-semibold text-2xl" onClick={() => veiwStatusReport(item.workstreamId, item.workstreamName)}>
                                    {item.workstreamName}
                                </div>
                                <div className="text-2xl whitespace-nowrap" onClick={() => veiwStatusReport(item.workstreamId, item.workstreamName)}>
                                    {getLifecycleStageName(item.lifecycleStage!)}
                                </div>
                            </div>

                            {/* Second Row: Other 5 Health Indicators */}
                            <div className="flex flex-row items-center justify-around p-2 gap-1 pb-2 mb-2">
                                <div className="flex flex-col items-center gap-1" onClick={() => handleScopeClick(item.scope, item.workstreamName, item.workstreamId)}>
                                    <span className="text-xl  mb-2">Overall Health</span>
                                    <TrafficLightComponent TrafficLightColour={item.overall} />
                                </div>
                                <div className="flex flex-col items-center gap-1" onClick={() => handleScopeClick(item.scope, item.workstreamName, item.workstreamId)}>
                                    <span className="text-xl mb-2">Scope</span>
                                    <TrafficLightComponent TrafficLightColour={item.scope} />
                                </div>
                                <div className="flex flex-col items-center gap-1" onClick={() => handleScheduleClick(item.workstreamId, item.schedule === 4 ? 0 : item.schedule * 128)}>
                                    <span className="text-xl mb-2">Schedule</span>
                                    <TrafficLightComponent TrafficLightColour={item.schedule} />
                                </div>
                                <div className="flex flex-col items-center gap-1" onClick={() => handleResourcesClick(item.workstreamId, item.resources, item.workstreamName)}>
                                    <span className="text-xl mb-2">Resources</span>
                                    <TrafficLightComponent TrafficLightColour={item.resources} />
                                </div>
                                <div className="flex flex-col items-center gap-1" onClick={() => handleFinancialClick(item.workstreamId, item.financial, item.workstreamName)}>
                                    <span className="text-xl mb-2">Financial</span>
                                    <TrafficLightComponent TrafficLightColour={item.financial} />
                                </div>
                                <div className="flex flex-col items-center gap-1" onClick={() => handleOtherClick(item.workstreamId, item.other, item.workstreamName)}>
                                    <span className="text-xl mb-2">Other</span>
                                    <TrafficLightComponent TrafficLightColour={item.other} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        );
    }

    return (
        <Card className="flex grow flex-col justify-center px-4 pb-2">
            <CardHeader className="items-center pb-0">
                <CardTitle className="text-xl">{isMultiProject ? "Project" : "Workstream"} Health</CardTitle>
            </CardHeader>

            <div className="flex flex-col min-w-[655px] border border-slate-400 rounded-xl my-2">
                <div className="flex flex-row border-b-2 border-slate-400 py-4 px-2 rounded-t-xl bg-table-header text-white font-bold">
                    {showProjectName && (
                        <div className="flex w-[25%]">
                            <h3 className="mx-auto">{isMultiProject ? "Project Name" : "Workstream Name"}</h3>
                        </div>
                    )}

                    {showLifecycleStage && (
                        <div className="flex w-[15%]">
                            <h3 className="mx-auto">Stage</h3>
                        </div>
                    )}
                    <div className={"flex flex-row grow"}>
                        <div className="w-1/6 flex-1 text-center"><h3>Overall</h3></div>
                        <div className="w-1/6 flex-1 text-center"><h3>Scope</h3></div>
                        <div className="w-1/6 flex-1 text-center"><h3>Schedule</h3></div>
                        <div className="w-1/6 flex-1 text-center"><h3>Resources</h3></div>
                        <div className="w-1/6 flex-1 text-center"><h3>Financial</h3></div>
                        <div className="w-1/6 flex-1 text-center"><h3>Other</h3></div>
                    </div>
                </div>

                {workstreamHealth && workstreamHealth.map((item, index) => (
                    <div key={index} className="flex flex-row p-2">
                        {showProjectName && (
                            <div className="flex w-[25%] whitespace-nowrap truncate justify-items-center text-center hover: cursor-pointer" onClick={() => veiwStatusReport(item.workstreamId, item.workstreamName)}>
                                <span className="mx-auto">{item.workstreamName}</span>
                            </div>
                        )}
                        {showLifecycleStage && (
                            <div className="flex w-[15%] whitespace-nowrap truncate justify-items-center text-center hover: cursor-pointer" onClick={() => veiwStatusReport(item.workstreamId, item.workstreamName)}>
                                <span className="mx-auto">{getLifecycleStageName(item.lifecycleStage!)}</span>
                            </div>
                        )}
                        <div className={"flex flex-row grow"}>
                            <div className="flex-1 w-1/6 justify-items-center" onClick={() => veiwStatusReport(item.workstreamId, item.workstreamName)}>
                                <TrafficLightComponent TrafficLightColour={item.overall} /></div>
                            <div className="flex-1 w-1/6 justify-items-center" onClick={() => handleScopeClick(item.scope, item.workstreamName, item.workstreamId)}>
                                <TrafficLightComponent TrafficLightColour={item.scope} /></div>
                            <div className="flex-1 w-1/6 justify-items-center" onClick={() => handleScheduleClick(item.workstreamId, item.schedule === 4 ? 0 : item.schedule * 128)}>
                                <TrafficLightComponent TrafficLightColour={item.schedule} /></div>
                            <div className="flex-1 w-1/6 justify-items-center" onClick={() => handleResourcesClick(item.workstreamId, item.resources, item.workstreamName)}>
                                <TrafficLightComponent TrafficLightColour={item.resources} /></div>
                            <div className="flex-1 w-1/6 justify-items-center" onClick={() => handleFinancialClick(item.workstreamId, item.financial, item.workstreamName)}>
                                <TrafficLightComponent TrafficLightColour={item.financial} /></div>
                            <div className="flex-1 w-1/6 justify-items-center" onClick={() => handleOtherClick(item.workstreamId, item.other, item.workstreamName)}><TrafficLightComponent TrafficLightColour={item.other} /></div>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}

export default WorkstreamHealthReport;